import React from 'react';

import { useNavLink, type UseNavLinkOptions } from '../../hooks/useNavLink';
import type { Route } from '../../types';

export interface NavLinkProps extends Omit<UseNavLinkOptions, 'route' | 'openInNewTab'> {
    /** Target route. */
    to: Route;
    [key: string]: any;
}

/**
 * A `Link` that knows whether it points at the page in view. It then carries `aria-current="page"`.
 *
 * Inside a `NavLinkGroup`, exactly one link of the group is current: the one with the longest
 * matched URL. Outside any group, the link matches itself, as react-router's own `NavLink` does.
 *
 * Can be used with a LumX component through its `linkAs` prop.
 * @example
 *  <NavLinkGroup>
 *      <Tab linkAs={NavLink} to={generalTabRoute} label="General" />
 *      <Tab linkAs={NavLink} to={membersTabRoute} label="Members" />
 *  </NavLinkGroup>
 *
 * @family Router
 */
export const NavLink = React.forwardRef<HTMLAnchorElement, NavLinkProps>((props, ref) => {
    const { to, id, target, children, onRedirect, routerData, onClick, keepFocus, replace, ...forwardedProps } = props;
    const { isCurrentPage, linkProps } = useNavLink({
        route: to,
        id,
        target,
        onClick,
        openInNewTab: target === '_blank',
        onRedirect,
        routerData,
        keepFocus,
        replace,
    });

    return (
        <a ref={ref} aria-current={isCurrentPage ? 'page' : undefined} {...forwardedProps} {...linkProps}>
            {children}
        </a>
    );
});
NavLink.displayName = 'NavLink';
